import { ChangeDetectionStrategy, Component, computed, effect, inject } from '@angular/core';
import { DatePipe } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { PlanPricesComponent } from '../../Partials/plan-prices/plan-prices.component';
import { PlanFeaturesComponent } from '../../Partials/plan-features/plan-features.component';
import { PlanSubscribersComponent } from '../../Partials/plan-subscribers/plan-subscribers.component';
import { AvEmptyStateComponent } from '../../../../Shared/Components/av-empty-state/av-empty-state.component';
import { AvStatusChipComponent } from '../../../../Shared/Components/av-status-chip/av-status-chip.component';
import { PlanDetailFacade } from '../../../../../Facades/Plans/plan-detail.facade';
import { DATE_TIME_FORMAT, VALUE_UNRESOLVED } from '../../../../../Consts/backoffice.const';
import {
  PLAN_DEFAULT_TAB,
  PLAN_NAME_MAX,
  PLAN_TABS,
  PLAN_TAB_QUERY_PARAM,
} from '../../../../../Consts/plans.const';
import { PlanDetail } from '../../../../../Models/backoffice/plan-detail.model';

@Component({
  selector: 'app-plan-detail',
  standalone: true,
  imports: [
    DatePipe,
    RouterLink,
    ReactiveFormsModule,
    MatIconModule,
    TranslateModule,
    PlanPricesComponent,
    PlanFeaturesComponent,
    PlanSubscribersComponent,
    AvEmptyStateComponent,
    AvStatusChipComponent,
  ],
  templateUrl: './plan-detail.component.html',
  styleUrl: './plan-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'd-block' },
})
export class PlanDetailComponent {
  private readonly _route = inject(ActivatedRoute);
  private readonly _router = inject(Router);
  private readonly _formBuilder = inject(FormBuilder);

  readonly facade = inject(PlanDetailFacade);

  readonly dateTimeFormat = DATE_TIME_FORMAT;
  readonly unresolved = VALUE_UNRESOLVED;
  readonly tabs = PLAN_TABS;

  readonly formGroup: FormGroup = this._formBuilder.group({
    name: ['', [Validators.required, Validators.maxLength(PLAN_NAME_MAX)]],
    is_active: [true],
  });

  private readonly _params = toSignal(this._route.paramMap);
  private readonly _query = toSignal(this._route.queryParamMap);

  readonly activeTab = computed<string>(
    () => this._query()?.get(PLAN_TAB_QUERY_PARAM) || PLAN_DEFAULT_TAB,
  );

  constructor() {
    this._registerPlanParam();
    this._registerPlanSync();
  }

  selectTab(tabId: string): void {
    this._router.navigate([], {
      relativeTo: this._route,
      queryParams: { [PLAN_TAB_QUERY_PARAM]: tabId },
      queryParamsHandling: 'merge',
    });
  }

  submit(): void {
    if (!this.formGroup.valid) {
      return;
    }

    this.facade.save({
      name: String(this.formGroup.value.name).trim(),
      is_active: Boolean(this.formGroup.value.is_active),
    });
  }

  private _registerPlanParam(): void {
    effect(() => {
      const planId = this._params()?.get('id') ?? '';

      this._loadPlan(planId);
    });
  }

  private _registerPlanSync(): void {
    effect(() => {
      const plan = this.facade.plan();

      this._applyPlan(plan);
    });
  }

  private _loadPlan(planId: string): void {
    this.facade.load(planId);
  }

  private _applyPlan(plan: PlanDetail | null): void {
    if (!plan) {
      return;
    }

    this.formGroup.reset({ name: plan.name ?? '', is_active: plan.is_active });
  }
}
